import React, { useId, useState } from "react";
import Paywall from "../../../common/Paywall";
import { Badge, Chip, Overline } from "../../../ForBrands/ui/BrandUI";
import { KeyValueRow, ListRule, MobileCard, MobileCardHead, MobileKpiTile } from "../../../ForBrands/ui/mobile/MobileUI";
import { SegmentedControl } from "../../../auth/v2/pieces";
import ShotZoneChart, { ShotZoneScale } from "../../PlayerProfile/sections/ShotZoneChart";
import PercentileRadar, { RADAR_EMPTY, RADAR_GROUPS_LABEL, RADAR_TITLE, radarCaption } from "../PercentileRadar";
import {
  CategoryTile,
  DEFAULT_AVATAR,
  MatchTile,
  PlayGroupSwitch,
  SeasonPointCard,
  StatsProvenance,
  TeamComparison,
  anchoredStyle,
  engagementBar,
  faceCrop,
  seasonPointText,
} from "../profileSections";
import { ENGAGEMENT_MARKS, engagementText } from "../profileNarratives";
import MetricInfo from "../MetricInfo";
import { METRIC_INFO } from "../metricDescriptions";
import { EMPTY_VALUE, fmtCompact, fmtMoney } from "../athleteUtils";
import { fmtIndex10 } from "../../../../utils/indexScore";
import iconMail from "../../../../assets/brand/icon-mail.svg";

// Phone pieces of My Profile (Figma ATHLETE MODE V2 mobile, 2000:373
// Overview, 2000:467 Performance, 2000:558 Visibility, 2000:649 Value,
// 2000:740 About). They draw from the same profile model and narratives as
// the desktop sections (profileSections.js, profileNarratives.js), in the
// mobile card style: 16px radius, the light #f2f2f4 border, bm-h-s titles
// with bm-body-xs subs. Presentational only, apart from the view switches
// (the radar's group, the current season's year and view, and the unused
// shot chart's encoding).

const cx = (...parts) => parts.filter(Boolean).join(" ");
const clampPct = (v) => Math.max(0, Math.min(100, Number(v) || 0));

// ------------------------------------------------------------ Overview
// Identity row (2020:1768): the 72px photo, the name as the page heading
// and the subtitle, then the page's actions (2020:1774) two up at 52px.
// Returns two rows, so they sit in the tab body's 18px rhythm. `badge` (the
// athlete's own profile: PlanBadge) sits beside the name and wraps under a
// long one.
export const ProfileIdentity = ({ model, subtitle, actions, badge }) => (
  <>
    <div className="flex items-center gap-[14px]">
      <img
        src={faceCrop(model.avatar || DEFAULT_AVATAR)}
        alt=""
        className="h-[72px] w-[72px] shrink-0 rounded-[16px] border border-bm-orange bg-bm-mutedbg object-cover object-top"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1 break-words">
        {badge ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="bm-h-l text-bm-ink">{model.fullName}</h1>
            {badge}
          </div>
        ) : (
          <h1 className="bm-h-l text-bm-ink">{model.fullName}</h1>
        )}
        {subtitle && <p className="bm-body-s text-bm-muted">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="flex gap-[10px]">{actions}</div>}
  </>
);

// The 72px CacheScore gauge (2276:8368): a 7px ring on the strong grey
// track, filled clockwise from 12 o'clock, the score in the middle.
export const MiniGauge = ({ value }) => {
  const r = 32.5;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : clampPct(value) / 100;
  return (
    <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center">
      <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true" focusable="false" className="absolute left-0 top-0 -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" strokeWidth="7" className="stroke-bm-strong" />
        {pct > 0 && (
          <circle
            cx="36"
            cy="36"
            r={r}
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
            className="stroke-bm-orange"
          />
        )}
      </svg>
      <span className="relative bm-h-l text-bm-ink">{value == null ? EMPTY_VALUE : Math.round(value)}</span>
    </div>
  );
};

// CacheScore card (2020:1779). Each metric card's (i) opens its
// description in a bottom sheet (MetricInfo, metricDescriptions).
export const ScoreCard = ({ model }) => (
  <MobileCard>
    <div className="flex items-center gap-3">
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <MobileCardHead
          title={METRIC_INFO.cacheScore.title}
          sub={METRIC_INFO.cacheScore.short}
          right={<MetricInfo metric="cacheScore" />}
          className="self-stretch"
        />
        {model.confidence && <Badge tone="outline">{`Confidence ${model.confidence}`}</Badge>}
      </div>
      <MiniGauge value={model.cacheScore} />
    </div>
  </MobileCard>
);

// A pillar out of 10 ("6.1/10", utils/indexScore), the bar filled to its 0
// to 100 score; the en dash for a pillar never computed.
const ContributionRow = ({ label, value }) => (
  <div className="flex flex-col gap-[6px]">
    <div className="flex items-center gap-3">
      <span className="min-w-0 flex-1 bm-body-m text-bm-muted">{label}</span>
      <span className="bm-label-l text-bm-ink">{fmtIndex10(value, { suffix: true })}</span>
    </div>
    <div className="h-[6px] w-full overflow-hidden rounded-full bg-bm-mutedbg">
      <div className="h-full rounded-full bg-bm-orange" style={{ width: `${clampPct(value)}%` }} />
    </div>
  </div>
);

// Contributions card (2020:1789); the web's "Off-court impact" casing.
export const ContributionsCard = ({ contributions }) => (
  <MobileCard className="!gap-[14px]">
    <MobileCardHead title={METRIC_INFO.contributions.title} sub={METRIC_INFO.contributions.short} right={<MetricInfo metric="contributions" />} />
    <ContributionRow label="Performance" value={contributions.performance} />
    <ContributionRow label="Visibility" value={contributions.visibility} />
    <ContributionRow label="Off-court impact" value={contributions.offCourt} />
  </MobileCard>
);

// The value tile and the CacheNILx tile (2020:1811), behind the same Paywall
// as the desktop valuation row. The value tile is the profile's one value
// module (D3): CacheValue with its badge under the amount, or, for an
// athlete who has not onboarded, PerformanceValue in the same place. The
// tile's name may wrap before "Value™", never inside a word: the
// "PERFORMANCEVALUE™" overline is wider than half a 320px phone.
const tileName = (title) =>
  String(title)
    .split(/(?=Value™$)/)
    .map((part, i) => (i ? <React.Fragment key={part}><wbr />{part}</React.Fragment> : part));

export const ValueTiles = ({ model, tierLabel, premium }) => {
  const card = model.valueCard;
  return (
    <Paywall
      requiredEntitlements={premium}
      title={card.paywallTitle}
      description="Upgrade to view market valuation and social influence value."
      showPreview
      showLockIcon={false}
    >
      <div className="grid grid-cols-2 gap-3">
        <MobileKpiTile label={tileName(card.title)} value={fmtMoney(card.range?.value)} sub={card.tileSub} />
        <MobileKpiTile label="CacheNILx™ Value" value={fmtMoney(model.cacheNilx?.value)} sub={tierLabel} />
      </div>
    </Paywall>
  );
};

// --------------------------------------------------------- Performance
// A chip that only shows a value (the sport, a season when it is the only
// one on file): the BrandUI Chip's active look (2020:1821, 2020:1956) on a
// span, since there is nothing to pick.
export const StaticChip = ({ children }) => (
  <span className="inline-flex h-10 items-center self-start whitespace-nowrap rounded-full bg-bm-n700 pl-4 pr-[14px] bm-label-l text-white">
    {children}
  </span>
);

// Percentile ranks as text, the state first (the desktop's blue badge),
// then the peer group. A narrow cell wraps between ranks, never inside one.
// Ranks that name their group (the model's `scope`: stand-in stats, a group
// of another level) take a line each, "Top 2% in NV" over "Top 3% in
// Nevada 5A 2026", the "Top N%" kept whole and the group wrapping after it.
const Ranks = ({ chips, className = "" }) =>
  chips.some((c) => c.scope) ? (
    <span className={cx("flex flex-col bm-body-xs text-bm-muted", className)}>
      {chips.map((c, i) => (
        <span key={`${c.variant}-${c.label}-${i}`}>
          <span className="whitespace-nowrap">{c.rank}</span>
          {c.label.slice(String(c.rank || "").length)}
        </span>
      ))}
    </span>
  ) : (
    <span className={cx("bm-body-xs text-bm-muted", className)}>
      {chips.map((c, i) => (
        <React.Fragment key={`${c.variant}-${c.label}-${i}`}>
          {i > 0 && " · "}
          <span className="whitespace-nowrap">{c.label}</span>
        </React.Fragment>
      ))}
    </span>
  );

// The legend of the ranks a card draws (`items` with their chips): "State ·
// Peer group", or "Peer group" when no state rank shows; none without ranks.
// Exported for the Performance section's compact box score.
export const RankLegend = ({ items, className = "" }) => {
  const chips = items.flatMap((s) => s.chips || []);
  if (!chips.length) return null;
  return <p className={cx("bm-body-xs text-bm-muted", className)}>{chips.some((c) => c.variant === "blue") ? "State · Peer group" : "Peer group"}</p>;
};

// Stat cell (2020:1829): left aligned on the subtle fill, the ranks as text.
export const MobileStatCell = ({ value, label, chips }) => (
  <div className="flex min-w-0 flex-col gap-[2px] break-words rounded-[12px] bg-bm-subtle p-3">
    <span className="bm-num-ml text-bm-ink">{value == null ? EMPTY_VALUE : value}</span>
    <span className="bm-overline">{label}</span>
    {chips?.length > 0 && <Ranks chips={chips} />}
  </div>
);

// Season box score (2020:1823): two columns of stat cells (an odd last cell
// sits alone), the subtitle naming the group and the line the cells show,
// and under it where the stats come from (the desktop's StatsProvenance).
// Before stats sync, the empty cells and the web's note. `line` is the
// line shown (the model's own, or one of its playGroups, D7); with two or
// more play groups the School | Grassroots switch under the head picks it
// (`onGroupChange(key)`).
export const BoxScoreCard = ({ model, subtitle, self, line = model, onGroupChange }) => (
  <MobileCard>
    <MobileCardHead title="Season box score" sub={subtitle} />
    {onGroupChange && <PlayGroupSwitch groups={model.playGroups} value={line.key} onChange={onGroupChange} compact />}
    <StatsProvenance model={model} compact />
    <div className="grid grid-cols-2 gap-[10px]">
      {line.boxScore.map((s) =>
        line.hasBoxScore ? (
          <MobileStatCell key={s.label} value={s.value} label={s.label} chips={s.chips} />
        ) : (
          <MobileStatCell key={s.label} value={null} label={s.label} />
        ),
      )}
    </div>
    {line.hasBoxScore ? (
      <RankLegend items={line.boxScore} className="pt-3" />
    ) : (
      <p className="bm-body-m text-bm-muted">
        {self
          ? "No season stats on file yet. Stats sync from your program's data partner once your coach verifies you, or add them under Edit my profile."
          : "No season stats on file yet. Stats appear once they sync from the athlete's program data partner."}
      </p>
    )}
  </MobileCard>
);

// Shooting efficiency (2020:1901), a card of its own on phones: value,
// name and ranks on one line, the definition under it, rules between.
// Ranks that name their group are too long for that line: they sit under
// it, a line each.
export const ShootingCard = ({ items, hasPercentiles }) => (
  <MobileCard>
    <MobileCardHead title="Shooting efficiency" sub="KenPom-style efficiency metrics, how well, not just how much" />
    {items.length ? (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
        <ul role="list" className="flex flex-col">
          {items.map((s, i) => {
            const chips = s.chips || [];
            const named = chips.some((c) => c.scope);
            return (
              <li key={s.label} className={cx("flex flex-col gap-1", i > 0 && "mt-3 border-t border-bm-mutedbg pt-3")}>
                <div className="flex items-center gap-3">
                  <span className="min-w-[52px] shrink-0 whitespace-nowrap bm-num-m text-bm-ink">{s.value ?? EMPTY_VALUE}</span>
                  <span className="min-w-0 flex-1 break-words bm-label-l text-bm-ink">{s.label}</span>
                  {chips.length > 0 && !named && <Ranks chips={chips} className="shrink-0 whitespace-nowrap" />}
                </div>
                {named && <Ranks chips={chips} className="break-words" />}
                <p className="bm-body-xs text-bm-muted">{s.desc}</p>
              </li>
            );
          })}
        </ul>
        {hasPercentiles && <RankLegend items={items} />}
      </>
    ) : (
      <p className="bm-body-s text-bm-muted">Efficiency metrics appear once shot attempts are on file.</p>
    )}
  </MobileCard>
);

const SHOT_VIEWS = [
  ["frequency", "Shot frequency"],
  ["potency", "Shot potency"],
];

// Shot zone chart (2020:1929): the web court (Figma leaves the art to be
// ported) with one encoding at a time, frequency as dot size or potency as
// colour (D-a10), and the low-to-high scale for the open view. Not drawn on
// any profile: its dots are generated, not real shot data (ShotZoneChart),
// so the player radar stands where it was at every level. Kept for when
// shot locations exist.
export const ShotZoneCard = ({ profile }) => {
  const [mode, setMode] = useState("frequency");
  return (
    <MobileCard>
      <MobileCardHead title="Shot zone chart" sub="Where the scoring comes from, and how efficiently, by court zone" />
      <SegmentedControl label="Shot chart view" value={mode} onChange={setMode} options={SHOT_VIEWS} />
      <ShotZoneChart profile={profile} mode={mode} bare />
      <ShotZoneScale mode={mode} />
    </MobileCard>
  );
};

// Player radar (the Figma note on 1504:5385, the coach radar's phone plot
// 2409:1225): a group's percentiles as one shape, captioned with the group,
// or why there is none yet, after shooting efficiency at every level.
// `groups` is the profile model's `radarGroups` (League, College, Pro);
// with two or more a Compare with switch picks one, League first.
export const RadarCard = ({ groups = [] }) => {
  const [picked, setPicked] = useState(null);
  const group = groups.find((g) => g.key === picked) || groups[0] || null;
  return (
    <MobileCard>
      <MobileCardHead title={RADAR_TITLE} sub={group ? radarCaption(group.peerLabel) : null} />
      {groups.length > 1 && (
        <SegmentedControl label={RADAR_GROUPS_LABEL} value={group.key} onChange={setPicked} options={groups.map((g) => [g.key, g.tab])} />
      )}
      {group ? <PercentileRadar axes={group.radar} peerLabel={group.peerLabel} /> : <p className="bm-body-s text-bm-muted">{RADAR_EMPTY}</p>}
    </MobileCard>
  );
};

// Bar pairs (2020:1970, D-a11), one per game or per event: the athlete in
// orange (lighter for a camp or all-star game, on the timeline but not in
// the season's numbers), the group average (`avgName`, "League avg" or
// "Position avg") in the grey of the vs. teammates chart, scaled to the
// tallest value in a 94px area. `unit` names the athlete's value: "points"
// for a game, "PPG" for an event's average. A label wraps between its words
// ("Game" over "6") rather than widening its column, so six games fit a
// 320px phone; a column is never narrower than its bars or the label's
// longest word, and past what fits (about seven games at 320px) the row
// scrolls sideways. The bar areas line up at the top, so a wrapped label
// never lifts a pair.
//
// Each pair is a toggle button (Kobi's 10/2 note: the opponent and the
// place on the timeline): a tap, a click, Enter or Space opens that game or
// event in a panel under the bars (SeasonPointCard, the desktop popover),
// one at a time; the same pair again or Escape closes it. The panel speaks
// politely as it changes. Each button reads the point in full
// (seasonPointText), so a screen reader has it without opening the panel.
export const SeasonBars = ({ series, name, avgName = "Average", unit = "PPG" }) => {
  const [open, setOpen] = useState(null);
  const panelId = useId();
  const values = series.flatMap((s) => [s.me, s.avg]).filter((v) => typeof v === "number" && Number.isFinite(v));
  const max = Math.max(0, ...values);
  const height = (v) => (typeof v === "number" && v > 0 && max > 0 ? `${Math.max(2, (v / max) * 100)}%` : "0%");
  const point = open != null ? series[open] : null;
  // The panel stays in the page while closed (empty, taking no space), so a
  // screen reader announces the first point it opens; the focus ring is
  // drawn inside each button, where the scrolling row cannot clip it.
  return (
    <div className="flex flex-col">
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
      <ul role="list" aria-label="Points per game" className="flex items-start gap-[6px] overflow-x-auto bm-no-scrollbar">
        {series.map((s, i) => (
          <li key={`${s.label}-${i}`} className="flex flex-1 flex-col items-stretch">
            <button
              type="button"
              aria-pressed={open === i}
              aria-controls={panelId}
              onClick={() => setOpen(open === i ? null : i)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && open != null) {
                  e.preventDefault();
                  setOpen(null);
                }
              }}
              className={cx(
                "flex flex-col items-center gap-[6px] rounded-[8px] px-[2px] pb-1 pt-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bm-ink",
                open === i ? "bg-bm-mutedbg" : "hover:bg-bm-subtle",
              )}
            >
              <span aria-hidden="true" className="flex h-[94px] items-end justify-center gap-[3px]">
                <span className={cx("w-3 rounded-t-[3px]", s.camp ? "bg-bm-orange/40" : "bg-bm-orange")} style={{ height: height(s.me) }} />
                {s.avg != null && <span className="w-3 rounded-t-[3px] bg-bm-strong" style={{ height: height(s.avg) }} />}
              </span>
              <span aria-hidden="true" className={cx("text-center bm-body-xs", open === i ? "text-bm-ink" : "text-bm-muted")}>
                {s.label}
              </span>
              <span className="sr-only">{seasonPointText(s, { name, avgName, unit })}</span>
            </button>
          </li>
        ))}
      </ul>
      <div id={panelId} aria-live="polite" className={point ? "pt-[10px]" : undefined}>
        {point && <SeasonPointCard point={point} name={name} avgName={avgName} unit={unit} compact />}
      </div>
    </div>
  );
};

// Exported for the Performance section's Peers and teammates card (phones).
export const LegendDot = ({ tone, children }) => (
  <span className="flex items-center gap-[6px] bm-body-xs text-bm-muted">
    <span aria-hidden="true" className={cx("h-2 w-2 shrink-0 rounded-full", tone)} />
    {children}
  </span>
);

// Current season (2020:1950): the seasons on file as year chips (one is a
// plain chip), a line per competition (a camp's tagged), the season's
// numbers (a note when they leave camp games out), the My stats / vs.
// teammates switch when the teammates cover the season, bar pairs (each
// opening its game or event) or the teammates chart with its legend, then
// the comparison and its rows when the peer group covers the season, else
// a note naming the season it does cover. `narratives` is
// profileNarratives().
export const SeasonCard = ({ model, narratives }) => {
  const [picked, setPicked] = useState(null);
  const [seasonView, setSeasonView] = useState("mine");
  const { self, cs, seasonFor } = narratives;
  const { season, subtitle, series, comparison, teammates, note } = seasonFor(picked);
  // A season the teammates do not cover has no teammates view.
  const view = teammates ? seasonView : "mine";
  const name = cs.firstName || (self ? "You" : "Athlete");
  const you = self ? "You" : cs.firstName || model.firstName || "Athlete";
  return (
    <MobileCard className="!gap-[14px]">
      <MobileCardHead title="Current season" sub={subtitle} />
      <div className="flex flex-col gap-2">
        <Overline>Select year</Overline>
        {cs.seasons.length > 1 ? (
          <div role="group" aria-label="Select year" className="flex flex-wrap gap-2">
            {cs.seasons.map((s) => (
              <Chip key={s.label} active={s.label === season.label} aria-pressed={s.label === season.label} onClick={() => setPicked(s.label)}>
                {s.label}
              </Chip>
            ))}
          </div>
        ) : (
          <div className="flex gap-2">
            <StaticChip>{season.label}</StaticChip>
          </div>
        )}
      </div>
      {season.competitions.length > 0 && (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
        <ul role="list" aria-label="Competitions" className="flex flex-col gap-1">
          {season.competitions.map((line, i) => (
            <li key={`${line}-${i}`} className="break-words bm-body-s text-bm-ink">
              {line}
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-[10px]">
        {season.numbers.map((n) => (
          <MobileStatCell key={n.label} value={n.value} label={n.label} />
        ))}
      </div>
      {season.campNote && <p className="bm-body-xs text-bm-muted">{season.campNote}</p>}
      {teammates && (
        <div className="flex flex-col gap-2">
          <Overline>Athlete view</Overline>
          <SegmentedControl
            label="Athlete view"
            value={view}
            onChange={setSeasonView}
            options={[
              ["mine", self ? "My stats" : "Season stats"],
              ["teammates", "vs. teammates"],
            ]}
          />
        </div>
      )}
      {view === "mine" && series.length > 0 && (
        <>
          <SeasonBars
            key={season.label}
            series={series}
            name={name}
            avgName={comparison?.avgName}
            unit={season.kind === "games" ? "points" : "PPG"}
          />
          <div className="flex flex-wrap gap-x-[14px] gap-y-1">
            <LegendDot tone="bg-bm-orange">{`${name} (PPG)`}</LegendDot>
            {comparison?.avgPpg != null && <LegendDot tone="bg-bm-strong">{comparison.avgName}</LegendDot>}
            {season.campLegend && <LegendDot tone="bg-bm-orange/40">{season.campLegend}</LegendDot>}
          </div>
        </>
      )}
      {view === "teammates" && (
        <>
          <TeamComparison teammates={teammates} label={you} compact />
          <div className="flex flex-wrap gap-x-[14px] gap-y-1">
            <LegendDot tone="bg-bm-orange">{you}</LegendDot>
            <LegendDot tone="bg-bm-strong">Team avg</LegendDot>
          </div>
        </>
      )}
      {comparison && (
        <>
          {comparison.caption && <p className="bm-body-xs text-bm-muted">{comparison.caption}</p>}
          {comparison.tiles.length > 0 && (
            <>
              <ListRule />
              <div className="flex flex-col gap-[14px]">
                {comparison.tiles.map((t) => (
                  <div key={t.label} className="flex items-center gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
                      <p className="bm-overline">{t.label}</p>
                      {t.chip?.label && <p className="bm-body-xs text-bm-muted">{t.chip.label}</p>}
                    </div>
                    <span className="shrink-0 whitespace-nowrap bm-num-m text-bm-ink">{t.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {note && <p className="bm-body-s text-bm-muted">{note}</p>}
    </MobileCard>
  );
};

// ---------------------------------------------------------------- Value
// CacheValue and CacheNILx range cards (2021:1818, 2021:1833): the badge in
// the head, the rate large, an 8px track without the desktop knob, and the
// floor and ceiling at the ends. Without a range the badge goes too, as the
// desktop RangeMeter shows it only beside a value. `badgeNote` names what
// the badge ranks against, under it ("Top 10%" over "College basketball"
// when the CacheValue's distribution pooled men and women). `metric` (a
// metricDescriptions key) puts the card's (i) after the badge.
export const MobileRangeCard = ({ title, sub, badge, badgeNote, range, metric }) => {
  const span = range ? Math.max(1, range.ceiling - range.floor) : 1;
  const pos = range ? clampPct(((range.value - range.floor) / span) * 100) : 0;
  const shown = badge && range;
  return (
    <MobileCard>
      <MobileCardHead
        title={title}
        sub={sub}
        right={
          <>
            {shown && badgeNote ? (
              <span className="flex max-w-[50%] shrink-0 flex-col items-end gap-1 break-words text-right">
                <Badge tone="outline">{badge}</Badge>
                <span className="bm-body-xs text-bm-muted">{badgeNote}</span>
              </span>
            ) : shown ? (
              <Badge tone="outline" className="shrink-0">
                {badge}
              </Badge>
            ) : null}
            {metric && <MetricInfo metric={metric} />}
          </>
        }
      />
      {range ? (
        <>
          <div className="flex flex-col gap-1">
            <p className="bm-overline">Suggested Market Rate</p>
            <p className="break-words bm-num-xl text-bm-ink">{fmtMoney(range.value)}</p>
          </div>
          <div aria-hidden="true" className="h-2 w-full overflow-hidden rounded-full bg-bm-mutedbg">
            <div className="h-full rounded-full bg-bm-orange" style={{ width: `${pos}%` }} />
          </div>
          <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 bm-body-xs text-bm-muted">
            <span>
              {fmtMoney(range.floor)} <span className="ml-1">Floor</span>
            </span>
            <span>
              {fmtMoney(range.ceiling)} <span className="ml-1">Ceiling</span>
            </span>
          </div>
        </>
      ) : (
        <p className="py-6 text-center bm-body-m text-bm-muted">Not available yet</p>
      )}
    </MobileCard>
  );
};

// NILx rate card (2021:1848): one label / value row per rate.
export const RateCard = ({ rates }) => (
  <MobileCard className="!gap-[10px]">
    <MobileCardHead title={METRIC_INFO.nilxRates.title} sub={METRIC_INFO.nilxRates.short} right={<MetricInfo metric="nilxRates" />} />
    <div className="flex flex-col">
      {rates.map((r) => (
        <KeyValueRow key={r.label} label={r.label} value={fmtMoney(r.value)} />
      ))}
    </div>
  </MobileCard>
);

// Brand category alignment (2021:1871): the desktop's fit cards, two up,
// with the spinner line while matching runs. The tab shows it on the
// desktop's condition (an audience read, matching, or matches).
export const BrandAlignmentCard = ({ model, narratives, matches }) => {
  const { self, hasMatches, matching, brandSubtitle } = narratives;
  const tiles = hasMatches
    ? matches.slice(0, 6).map((m) => <MatchTile key={m.domain || m.brand} match={m} selfView={self} />)
    : (model.brand?.categories || []).map((c) => <CategoryTile key={c.mono} category={c} />);
  return (
    <MobileCard>
      <MobileCardHead title="Brand category alignment" sub={brandSubtitle} />
      {matching && (
        <span className="flex items-center gap-3 bm-body-s text-bm-muted">
          <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-bm-border border-t-bm-orange" />
          {self ? "Matching brands to your profile…" : "Matching brands to this profile…"}
        </span>
      )}
      {tiles.length > 0 && <div className="grid grid-cols-2 gap-[10px]">{tiles}</div>}
    </MobileCard>
  );
};

// ---------------------------------------------------------- Visibility
// Letter marks in the platform colours (2021:1769). Facebook and Snapchat
// keep the colours of their desktop tiles; anything else gets an ink tile.
const PLATFORM_MARKS = {
  instagram: { code: "IG", background: "#e1306c" },
  tiktok: { code: "TT" },
  x: { code: "X", background: "#9e9ea3" },
  twitter: { code: "X", background: "#9e9ea3" },
  youtube: { code: "YT", background: "#ff0000" },
  facebook: { code: "FB", background: "#1877F2" },
  snapchat: { code: "SC", background: "#FFFC00", inkText: true },
  hudl: { code: "HU" },
};

const PlatformMark = ({ platform }) => {
  const mark = PLATFORM_MARKS[platform.key] || { code: String(platform.name || "").slice(0, 2).toUpperCase() };
  return (
    <span
      aria-hidden="true"
      className={cx(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bm-label-xs",
        mark.background ? null : "bg-bm-ink",
        mark.inkText ? "text-bm-ink" : "text-white",
      )}
      style={mark.background ? { background: mark.background } : undefined}
    >
      {mark.code}
    </span>
  );
};

// One platform (2021:1769). A linked platform shows its engagement (or
// "Linked") and follower count; the 30-day trend in the frame is not in the
// data yet. On the athlete's own page an unlinked platform opens Socials.
export const PlatformRow = ({ platform, onAdd }) => {
  const sub = platform.linked
    ? platform.engagement != null
      ? `${platform.engagement.toFixed(1)}% engagement`
      : "Linked"
    : "Not linked";
  const body = (
    <>
      <PlatformMark platform={platform} />
      <span className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
        <span className="flex flex-wrap items-center gap-2">
          <span className="bm-h-xs text-bm-ink">{platform.name}</span>
          {platform.linked && platform.verified && (
            <Badge tone="outline" dot="#1f9d55">
              Verified
            </Badge>
          )}
        </span>
        <span className="bm-body-xs text-bm-muted">{sub}</span>
      </span>
      {platform.linked ? (
        <span className="shrink-0 bm-num-m text-bm-ink">{fmtCompact(platform.followers)}</span>
      ) : (
        <span aria-hidden="true" className="shrink-0 bm-num-m text-bm-muted">
          {EMPTY_VALUE}
        </span>
      )}
    </>
  );
  if (!platform.linked && onAdd) {
    return (
      <button
        type="button"
        onClick={onAdd}
        title={`Add your ${platform.name} to boost your score`}
        className="flex w-full items-center gap-3 py-3 text-left"
      >
        {body}
      </button>
    );
  }
  return <div className="flex items-center gap-3 py-3">{body}</div>;
};

// Visibility footprint card (2021:1765): every platform the model has,
// ruled apart.
export const VisibilityCard = ({ platforms, onAdd }) => (
  <MobileCard>
    <MobileCardHead title="Visibility footprint" sub="Reach and engagement across platforms" />
    {platforms.map((p, i) => (
      <React.Fragment key={p.key}>
        {i > 0 && <ListRule />}
        <PlatformRow platform={p} onAdd={onAdd} />
      </React.Fragment>
    ))}
  </MobileCard>
);

// Engagement rate against the position benchmarks (2021:1800), on the
// desktop meter's scale (utils/benchmarkScale, D5), without the knob: the
// rate over the fill's end, the Avg and Top 10% marks as ticks where they
// really sit and their labels under the track growing away from each
// other; past three times the top mark the axis breaks.
export const EngagementCard = ({ engagement }) => {
  const rate = typeof engagement === "number" && Number.isFinite(engagement) ? engagement : null;
  const { scale, label } = engagementBar(rate);
  const shown = engagementText(rate);
  return (
    <MobileCard>
      <h2 className="bm-h-s text-bm-ink">Engagement rate vs. position benchmarks</h2>
      <div className="flex items-baseline gap-[10px]">
        <span className="bm-num-l text-bm-ink">{shown}</span>
        <span className="min-w-0 flex-1 bm-body-s text-bm-muted">Current engagement rate</span>
      </div>
      <div role="img" aria-label={label} className="flex flex-col gap-[6px]">
        <div className="relative h-4 bm-body-xs">
          {scale.valuePos != null && (
            <span className="absolute top-0 whitespace-nowrap text-bm-ink" style={anchoredStyle(scale.valuePos, scale.valueAnchor)}>
              {shown}
            </span>
          )}
        </div>
        <div className="relative h-2 w-full rounded-full bg-bm-mutedbg">
          {scale.valuePos != null && <div className="absolute left-0 top-0 h-full rounded-full bg-bm-orange" style={{ width: `${scale.valuePos}%` }} />}
          {scale.breakPos != null && (
            <span
              aria-hidden="true"
              className="absolute top-1/2 h-3 w-[6px] -translate-x-1/2 -translate-y-1/2 border-x-2 border-bm-strong bg-white"
              style={{ left: `${scale.breakPos}%` }}
            />
          )}
          {[
            ["low", scale.lowPos],
            ["high", scale.highPos],
          ]
            .filter(([, p]) => p != null)
            .map(([key, p]) => (
              <span key={key} aria-hidden="true" className="absolute top-1/2 h-[14px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-bm-muted" style={{ left: `${p}%` }} />
            ))}
        </div>
        <div className="relative h-4 bm-body-xs text-bm-muted">
          {scale.lowPos != null && (
            <span className="absolute left-0 top-0 min-w-max whitespace-nowrap text-right" style={{ width: `${scale.lowPos}%` }}>
              {`${engagementText(ENGAGEMENT_MARKS.low, { mark: true })} ${ENGAGEMENT_MARKS.lowLabel}`}
            </span>
          )}
          {scale.highPos != null && (
            <span className="absolute top-0 whitespace-nowrap" style={{ left: `${scale.highPos}%` }}>
              {`${engagementText(ENGAGEMENT_MARKS.high, { mark: true })} ${ENGAGEMENT_MARKS.highLabel}`}
            </span>
          )}
        </div>
      </div>
    </MobileCard>
  );
};

// ---------------------------------------------------------------- About
// Profile card (2021:1940): the bio, without the desktop's orange rule.
export const BioCard = ({ bio, onEdit }) => (
  <MobileCard>
    <h2 className="bm-h-m text-bm-ink">Profile</h2>
    <Overline>Bio</Overline>
    {bio ? (
      <p className="bm-body-m text-bm-muted">{bio}</p>
    ) : onEdit ? (
      <p className="bm-body-m text-bm-muted">
        No bio yet.{" "}
        <button type="button" onClick={() => onEdit("photo")} className="text-bm-orange underline underline-offset-2">
          Add one
        </button>{" "}
        so brands know your story.
      </p>
    ) : (
      <p className="bm-body-m text-bm-muted">No bio yet.</p>
    )}
  </MobileCard>
);

// Details card (2021:1944): one row per profile field, the web's field list.
export const DetailsCard = ({ fields }) => (
  <MobileCard density="list" className="!py-[14px]">
    {fields.length > 0 ? (
      fields.map((f) => (
        <KeyValueRow key={f.label} label={<span className="uppercase">{f.label}</span>} value={f.unit ? `${f.value} ${f.unit}` : f.value} />
      ))
    ) : (
      <p className="py-[11px] bm-body-m text-bm-muted">No profile details yet.</p>
    )}
  </MobileCard>
);

// Recruiting links (247Sports, On3). The phone page has no header link row,
// so About lists them, each its own link. Nothing when there are none.
export const RecruitingLinksCard = ({ links }) =>
  links?.length ? (
    <MobileCard density="list" className="!py-[14px]">
      <h2 className="py-[11px] bm-h-s text-bm-ink">Recruiting links</h2>
      {links.map((l, i) => (
        <KeyValueRow
          key={l.key}
          label={<span className="uppercase">{l.name}</span>}
          value={
            <a href={l.url} target="_blank" rel="noopener noreferrer" aria-label={`${l.name} profile`} className="text-bm-orange underline underline-offset-2">
              View profile
            </a>
          }
          last={i === links.length - 1}
        />
      ))}
    </MobileCard>
  ) : null;

// One circle member (ContactRow 2021:1992). "Primary" marks the athlete's
// point of contact, the only members whose email the profile shares.
export const ContactRow = ({ member }) => (
  <div
    className={cx(
      "flex items-center gap-3 rounded-[8px] border bg-white px-[14px] py-3",
      member.primary ? "border-bm-strong" : "border-bm-border",
    )}
  >
    <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <div className="flex flex-wrap items-center gap-[7px]">
        <Overline>{member.role}</Overline>
        {member.primary && (
          <Badge tone="outline" dot="#FF4400">
            Primary
          </Badge>
        )}
      </div>
      <span className="truncate bm-h-s text-bm-ink">{member.name}</span>
    </div>
    {member.email && (
      <a
        href={`mailto:${member.email}`}
        aria-label={`Email ${member.name}`}
        className="-mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] opacity-60 hover:opacity-100"
      >
        <img src={iconMail} alt="" className="h-[18px] w-[18px]" />
      </a>
    )}
  </div>
);

// Circle card (2021:1970). No Manage link: Circle is in the bottom nav.
export const CircleCard = ({ circle, selfView }) => (
  <MobileCard>
    <div className="flex items-center gap-3">
      <h2 className="min-w-0 flex-1 bm-h-s text-bm-ink">Circle</h2>
      {circle.length > 0 && <span className="bm-body-s text-bm-muted">{circle.length}</span>}
    </div>
    {circle.length === 0 ? (
      <p className="bm-body-s text-bm-muted">
        {selfView ? "No circle members added yet. Add your rep, guardian and coaches in My Circle." : "No circle members added yet."}
      </p>
    ) : (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" className="flex flex-col gap-3">
        {circle.map((m, i) => (
          <li key={`${m.role}-${m.name}-${i}`}>
            <ContactRow member={m} />
          </li>
        ))}
      </ul>
    )}
  </MobileCard>
);
